import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { BrandMark, Field } from "@/components/common";

const HERO = "https://images.unsplash.com/photo-1558362344-251a0a7d2a47?fm=jpg&q=70&w=1400";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(mobile, password);
      nav(u.must_change_password ? "/change-password" : loc.state?.from || "/dashboard", { replace: true });
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#0B1B36] lg:block">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <p className="font-display text-sm font-bold uppercase tracking-[0.25em] text-amber-400">PERFORMED REAL ESTATE LLP</p>
          <div className="max-w-md">
            <h1 className="font-display text-5xl font-extrabold leading-tight tracking-tight">Every lead.<br />Every listing.<br />One team.</h1>
            <p className="mt-4 text-white/70">Track buyers, sellers and properties on the go — built for the partners and team of PERFORMED.</p>
          </div>
          <p className="text-xs text-white/50">Lead Tracker · Mohali · Chandigarh · Tricity</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <BrandMark />
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight">Sign in</h2>
            <p className="mt-1 text-sm text-muted-foreground">Use your mobile number and password.</p>
          </div>
          <Field label="Mobile number">
            <div className="flex h-11 items-center rounded-lg border border-input bg-card focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
              <span className="border-r px-3 text-sm text-muted-foreground">+91</span>
              <input
                data-testid="login-mobile-input"
                inputMode="numeric"
                autoComplete="username"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                placeholder="98XXXXXXXX"
                className="h-full flex-1 bg-transparent px-3 text-sm outline-none num"
                required
              />
            </div>
          </Field>
          <Field label="Password">
            <div className="relative">
              <Input
                data-testid="login-password-input"
                type={show ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 pr-10"
                required
              />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-3 text-muted-foreground" data-testid="login-toggle-password">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          {err && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">{err}</p>}
          <Button type="submit" disabled={busy || mobile.length !== 10} className="h-11 w-full font-semibold" data-testid="login-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign in
          </Button>
          <div className="text-center">
            <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <p className="text-center text-xs text-muted-foreground">No public sign-up. Accounts are created by the Admin or Partners.</p>
        </form>
      </div>
    </div>
  );
}
